import React, { useRef, useState } from 'react';
import { uploadFile, analyzeStyle, errMsg } from './aiPaperApi';
import { ui } from './ui';

// Convert an extracted style profile into the wizard's blueprint shape (max 4 sections: A-D).
export function profileToBlueprint(profile) {
  const names = ['A', 'B', 'C', 'D'];
  const used = (profile.sections || []).slice(0, 4).map((s, i) => {
    let rows = s.rows.map((r) => ({ ...r }));
    let extra = 0;
    const total = rows.reduce((a, r) => a + r.count, 0);
    if (s.attemptAny > 0 && s.attemptAny < total && rows.length === 1) {
      extra = total - s.attemptAny;
      rows = [{ ...rows[0], count: s.attemptAny }];
    }
    return { name: names[i], enabled: true, rows, extra };
  });
  const rest = names.slice(used.length).map((n) => ({ name: n, enabled: false, rows: [{ type: 'Short', count: 2, marks: 2 }], extra: 0 }));
  return [...used, ...rest];
}

// Optional "clone the style of a sample paper" step shown in the Blueprint screen.
function StylePanel({ user, config, style, onStyle, onApply }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const ref = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    setErr('');
    try {
      setBusy('Uploading sample…');
      const doc = await uploadFile(user, file, { category: 'style_sample', subject: config.subject, class: config.class });
      setBusy('Analysing layout, marks, tone & difficulty…');
      const { styleProfile } = await analyzeStyle(user, doc._id);
      onStyle({ docId: doc._id, name: file.name, profile: styleProfile });
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(''); }
  };

  const p = style?.profile;
  return (
    <div style={{ ...ui.card, marginBottom: 14, borderColor: '#c4b5fd', background: '#faf5ff' }}>
      <div style={{ fontWeight: 900, color: '#6d28d9', marginBottom: 4 }}>🎨 Clone a sample paper's style (optional)</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
        Upload a previous question paper (PDF or image). The AI copies its format, question mix, marks, tone and difficulty — and writes brand-new questions from your chapter.
      </div>
      <input ref={ref} type="file" accept="application/pdf,.pdf,image/png,image/jpeg,image/webp" style={{ display: 'none' }}
        onChange={(e) => { handle(e.target.files[0]); e.target.value = ''; }} />
      <button style={ui.btnGhost} disabled={!!busy} onClick={() => ref.current?.click()}>
        {busy || (p ? 'Replace sample paper' : '📎 Upload sample paper')}
      </button>
      {p && !busy && (
        <button style={{ ...ui.btnSmall, marginLeft: 6 }} onClick={() => onStyle(null)}>Remove</button>
      )}
      {err && <div style={ui.errorBox}>{err}</div>}
      {p && (
        <div style={{ ...ui.okBox, marginTop: 10, fontSize: 13 }}>
          <div style={{ fontWeight: 800 }}>✅ {style.name}</div>
          <div>Difficulty: <b>{p.difficulty.level}</b> · Max marks: <b>{p.maxMarks || '?'}</b> · Duration: <b>{p.duration || '?'}</b></div>
          <div>Sections: {p.sections.map((s) => `${s.name} (${s.rows.map((r) => `${r.count}×${r.type}@${r.marks}`).join(', ')})`).join(' · ')}</div>
          {p.tone && <div>Tone: {p.tone}</div>}
          <button style={{ ...ui.btnPrimary, padding: '7px 14px', marginTop: 8 }} onClick={() => onApply(p)}>Apply this structure to blueprint</button>
        </div>
      )}
    </div>
  );
}

export default StylePanel;
